<div class="max-w-[760px] mx-auto">
    <div class="w-full space-y-6 md:space-y-8">
        <!-- Header Section -->
        <div class="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-2">
            <div class="space-y-1">
                <h3 class="text-xl md:text-2xl lg:text-3xl text-gray_10 font-bold leading-tight">Languages</h3>
                <p class="text-sm md:text-base text-gray_5 font-medium">Tell us what languages you can teach in or speak fluently.</p>
            </div>
            <div *ngIf="languages.length < 5" 
                 class="inline-flex items-center gap-1.5 font-bold text-primary hover:text-blue-700 cursor-pointer transition-colors mb-0.5" 
                 (click)="addLanguages()">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
                </svg>
                <span>Add language</span>
            </div>
        </div>

        <form class="w-full space-y-8" [formGroup]="languagesForm">
            <div class="w-full space-y-8" formArrayName="languages">
                <!-- Single Row Layout -->
                <div *ngFor="let item of languages.controls; let i = index" 
                     [formGroupName]="i" 
                     class="relative animate-fadeIn space-y-2">
                    
                    <!-- CSS Grid for Perfect Alignment -->
                    <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_48px] gap-3 lg:gap-4 items-end w-full">
                        <!-- Language Selection -->
                        <div class="space-y-2">
                            <label class="block text-dark text-sm lg:text-16 font-semibold">Language <span class="text-red-500">*</span></label>
                            <div class="w-full border_dropdown">
                                <p-dropdown [options]="getFilteredLanguages(i)" 
                                            formControlName="language" 
                                            optionLabel="name" 
                                            optionValue="id" 
                                            placeholder="Select Language"
                                            [filter]="true"
                                            filterBy="name"
                                            appendTo="body"
                                            panelStyleClass="languages-dropdown-panel"
                                            styleClass="w-full"></p-dropdown>
                            </div>
                        </div>

                        <!-- Proficiency Selection -->
                        <div class="space-y-2">
                            <label class="block text-dark text-sm lg:text-16 font-semibold">Proficiency <span class="text-red-500">*</span></label>
                            <div class="w-full border_dropdown">
                                <p-dropdown [options]="constants.languagesListObjArray" 
                                            formControlName="level" 
                                            optionLabel="name" 
                                            optionValue="value" 
                                            placeholder="Select Level"
                                            styleClass="w-full"></p-dropdown>
                            </div>
                        </div>

                        <!-- Trash Icon (SVG) -->
                        <div class="h-[48px] flex items-center justify-center min-w-[48px] self-end mb-1" *ngIf="languages.length > 1">
                            <button type="button" 
                                    (click)="removeLanguages(i)" 
                                    class="text-gray-400 hover:text-red-500 transition-colors p-2"
                                    title="Remove language">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                        <div *ngIf="languages.length <= 1" class="hidden md:block w-[48px]"></div>
                    </div>

                    <!-- Mirroring Grid for Error Alignment -->
                    <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_48px] gap-3 lg:gap-4 w-full">
                        <div class="w-full">
                            <div *ngIf="item.get('language')?.invalid && (item.get('language')?.dirty || item.get('language')?.touched)" class="text-red-500 text-xs mt-1">
                                Language is required.
                            </div>
                        </div>
                        <div class="w-full">
                            <div *ngIf="item.get('level')?.invalid && (item.get('level')?.dirty || item.get('level')?.touched)" class="text-red-500 text-xs mt-1">
                                Proficiency level is required.
                            </div>
                        </div>
                        <div class="hidden md:block w-[48px]"></div> <!-- Spacer mapping to Trash Icon track -->
                    </div>
                </div>
            </div>

            <p class="text-xs text-gray_5 font-medium italic opacity-75">You can add up to 5 languages.</p>

            <!-- Navigation Actions -->
            <div class="flex items-center gap-4 mt-6">
                <button type="button" class="flex-1 flex items-center justify-center text-base font-medium border border-gray_3 bg-white text-gray_10 hover:bg-gray_7 outline-none rounded-lg anim p-4" (click)="goBack()">
                    Back
                </button>
                <button type="button" class="flex-1 flex items-center justify-center text-base font-medium border border-primary bg-primary hover:bg-white text-white hover:text-primary outline-none rounded-lg anim p-4 disabled:opacity-50 disabled:cursor-not-allowed" 
                        (click)="updateLanguages()" 
                        [disabled]="languagesForm.invalid || isBtnLoading">
                    <span class="inline-block" *ngIf="isBtnLoading">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-inherit" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </span>
                    <span>Save And Continue</span>
                </button>
            </div>
        </form>
    </div>
</div>
